import { parseDecimal } from "./money";
import type { components } from "../types/generated/api";

type SourceWindow = components["schemas"]["CoachingSourcePolicyWindow"];

/** Plain-language rendering of the Facility policy frozen into a coaching offer. */
export function coachingCancellationSummary(policy: {
  source_policy_id?: string | null;
  source_windows: SourceWindow[] | null | undefined;
  session_bands?: { minimum_notice_minutes: number; refund_basis_points: number }[];
}): string[] {
  if (policy.source_policy_id === null) {
    const bands = policy.session_bands;
    if (!bands?.length) return ["Agreed cancellation terms unavailable. Refresh before accepting."];
    const notice = (minutes: number) => minutes % 1440 === 0 && minutes > 0
      ? `${minutes / 1440} ${minutes === 1440 ? "day" : "days"}`
      : minutes % 60 === 0 && minutes > 0
        ? `${minutes / 60} ${minutes === 60 ? "hour" : "hours"}`
        : `${minutes} minutes`;
    return bands.map((band, index) => {
      const refund = band.refund_basis_points / 100;
      const refundLabel = refund === 100 ? "full refund" : refund === 0 ? "no refund"
        : `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(refund)}% refund`;
      const label = band.minimum_notice_minutes > 0
        ? `At least ${notice(band.minimum_notice_minutes)} before`
        : index > 0 ? `Within ${notice(bands[index - 1].minimum_notice_minutes)} of start`
          : "Cancellation";
      return `${label}: ${refundLabel}`;
    });
  }
  if (!policy.source_windows) return ["Cancellation terms unavailable. Refresh before accepting."];
  const windows = [...policy.source_windows].sort((left, right) => right.hours_before - left.hours_before);
  if (!windows.length) return ["Full refund before the session"];
  if (new Set(windows.map((window) => window.hours_before)).size !== windows.length
      || windows.some((window) => !Number.isSafeInteger(window.hours_before) || window.hours_before < 0
        || parseDecimal(window.fee_percentage) === null)) {
    return ["Cancellation terms unavailable. Refresh before accepting."];
  }
  const first = windows[0].hours_before;
  const hourLabel = (hours: number) => `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const rows = [`More than ${hourLabel(first)} before: full refund`];
  for (const window of windows) {
    const fee = parseDecimal(window.fee_percentage)!;
    if (fee < 0 || fee > 100) return ["Cancellation terms unavailable. Refresh before accepting."];
    const refund = 100 - fee;
    const refundLabel = refund === 100 ? "full refund" : refund === 0 ? "no refund"
      : `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(refund)}% refund`;
    rows.push(`${window.hours_before === 0 ? "At session start" : `Within ${hourLabel(window.hours_before)}`}: ${refundLabel}`);
  }
  return rows;
}
